import React, { useEffect, useState } from 'react';
import { DocumentTextIcon, ExclamationTriangleIcon, PrinterIcon } from '@heroicons/react/24/outline';
import CloseButton from './CloseButton';
import MenuItemImage from './MenuItemImage';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../config/currency';
import SpiceLevelBadge from './SpiceLevelBadge';
import { DisplayContext, formatOrderId, getOrderTypeDisplay } from '../utils/orderFormatter';
import api from '../api/api';
import { printKOT, KOTData } from '../services/localPrintService';
import toast from 'react-hot-toast';
import { CURRENCY } from '../config/currency';

// Payment status check response from API
interface PaymentStatusResponse {
    needsPaymentConfirmation: boolean;
    sessionTotalAmount?: number;
    sessionPaidAmount?: number;
    sessionRemainingAmount?: number;
    isLastIncompleteOrder?: boolean;
    /** Discount on the session, if any — shown in the Confirm Payment popup */
    sessionDiscountAmount?: number;
}

// Interfaces (copied from VendorOrders.tsx to ensure compatibility)
interface OrderItem {
    id: number;
    menuItem?: {
        id: number;
        name: string;
        price: number;
        description?: string;
        imageUrl?: string;
    };
    itemName?: string;
    imageUrl?: string;
    quantity: number;
    price: number;
    comment?: string;
    toppings?: { id: number; name: string }[] | string[];
    portionName?: string;
    requestedSpiceLevel?: string;
    spiceLevel?: string;
}

interface Order {
    id: number;
    status: string;
    createdAt: string;
    updatedAt: string;
    orderNumber?: string;
    user?: { id: number; username: string; email?: string } | null;
    vendor?: { id: number; username: string };
    qrCode?: string | null;
    items: OrderItem[];
    total?: number;
    deliveryType?: string;
    position?: number;
    acceptedAt?: string;
    preparingAt?: string;
    readyAt?: string;
    completedAt?: string;
    guestName?: string;
    guestPhone?: string;
    guestEmail?: string;
    isGuestOrder?: boolean;
    customerName?: string;
    customerPhone?: string;
    customerEmail?: string;
    tableName?: string;
    floorName?: string;
    sectionName?: string;
    customOrderId?: string;
    cancellationReason?: string;
    cancelledAt?: string;
    orderNotes?: string; // Order-level special instructions
    // Session (OrderLine) payment info
    orderLineId?: number;
    sessionTotalAmount?: number;
    sessionPaidAmount?: number;
    sessionRemainingAmount?: number;
    isSessionFullyPaid?: boolean;
    orderLineStatus?: string;
    isLastIncompleteOrder?: boolean;
}

interface OrderAnalytics {
    orderToAcceptedMinutes?: number;
    orderToAcceptedTime?: string;
    acceptedToPreparingMinutes?: number;
    acceptedToPreparingTime?: string;
    preparingToReadyMinutes?: number;
    preparingToReadyTime?: string;
    readyToCompletedMinutes?: number;
    readyToCompletedTime?: string;
    totalMinutes?: number;
    totalTime?: string;
    timestamps: {
        createdAt?: string;
        acceptedAt?: string;
        preparingAt?: string;
        readyAt?: string;
        completedAt?: string;
    };
}

interface KOTItem {
    id: number;
    itemName: string;
    quantity: number;
    specialInstructions?: string;
    status: string;
    portion?: string;
}

interface KOT {
    id: number;
    kotNumber: string;
    orderId: number;
    kitchenId: number;
    kitchenName: string;
    status: string;
    priority: string;
    tableNumber?: string;
    customerName?: string;
    specialInstructions?: string;
    createdAt: string;
    printedAt?: string;
    acknowledgedAt?: string;
    startedAt?: string;
    readyAt?: string;
    servedAt?: string;
    printCount: number;
    items: KOTItem[];
}

interface OrderDetailModalProps {
    selectedOrder: Order | null;
    onClose: () => void;
    theme: any;
    modalLoading: boolean;
    orderAnalytics: OrderAnalytics | null;
    orderKOTs: KOT[];
    expandedKOTs: Set<number>;
    setExpandedKOTs: React.Dispatch<React.SetStateAction<Set<number>>>;
    updatingKOTs: Set<number>;
    handleKOTStatusUpdate: (kotId: number, status: string) => void;
    handleStatusUpdateInModal: (orderId: number, status: string, options?: { paymentConfirmed?: boolean }) => void;
    setInvoiceOrderId: (orderId: number | null) => void;
    setShowInvoiceModal: (show: boolean) => void;
    setInvoiceIncludeCompleted?: (include: boolean) => void;
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
    selectedOrder,
    onClose,
    theme,
    modalLoading,
    orderAnalytics,
    orderKOTs,
    expandedKOTs,
    setExpandedKOTs,
    updatingKOTs,
    handleKOTStatusUpdate,
    handleStatusUpdateInModal,
    setInvoiceOrderId,
    setShowInvoiceModal,
    setInvoiceIncludeCompleted
}) => {
    const navigate = useNavigate();

    // State for payment confirmation popup
    const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
    const [paymentInfo, setPaymentInfo] = useState<PaymentStatusResponse | null>(null);
    const [checkingPayment, setCheckingPayment] = useState(false);

    // State for KOT printing
    const [printingKOTs, setPrintingKOTs] = useState<Set<number>>(new Set());

    // Handle KOT print
    const handlePrintKOT = async (kot: KOT, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent expanding the KOT

        if (printingKOTs.has(kot.id)) return; // Already printing

        setPrintingKOTs(prev => new Set(prev).add(kot.id));

        try {
            // Build KOT data for printing
            const kotData: KOTData = {
                kotNumber: kot.kotNumber,
                id: kot.id,
                orderId: kot.orderId,
                orderNumber: selectedOrder?.orderNumber || selectedOrder?.customOrderId,
                tableNumber: kot.tableNumber || selectedOrder?.tableName,
                kitchenId: kot.kitchenId,
                kitchenName: kot.kitchenName,
                customerName: kot.customerName || selectedOrder?.guestName || selectedOrder?.user?.username,
                specialInstructions: kot.specialInstructions,
                priority: kot.priority,
                status: kot.status,
                items: kot.items.map(item => ({
                    itemName: item.itemName,
                    quantity: item.quantity,
                    portion: item.portion,
                    specialInstructions: item.specialInstructions
                })),
                createdAt: kot.createdAt
            };

            const result = await printKOT(kotData);

            if (result.success) {
                toast.success(`KOT ${kot.kotNumber} sent to printer`);
            } else {
                toast.error(result.error || 'Failed to print KOT');
            }
        } catch (err) {
            console.error('[PrintKOT] Error:', err);
            toast.error('Failed to connect to printer');
        } finally {
            setPrintingKOTs(prev => {
                const updated = new Set(prev);
                updated.delete(kot.id);
                return updated;
            });
        }
    };

    // Handle escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (showPaymentConfirmation) {
                    setShowPaymentConfirmation(false);
                } else {
                    onClose();
                }
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose, showPaymentConfirmation]);

    if (!selectedOrder) return null;

    // Handle status update with real-time payment confirmation check
    const handleStatusClick = async (status: string) => {
        if (status === 'COMPLETED') {
            // Make real-time check if payment confirmation is needed
            setCheckingPayment(true);
            try {
                const response = await api.get<PaymentStatusResponse>(`/orders/${selectedOrder.id}/check-payment-status`);
                const data = response.data;

                if (data.needsPaymentConfirmation) {
                    // Show confirmation popup with real-time data
                    setPaymentInfo(data);
                    setShowPaymentConfirmation(true);
                } else {
                    // No confirmation needed - complete directly
                    handleStatusUpdateInModal(selectedOrder.id, status);
                }
            } catch (error) {
                console.error('Error checking payment status:', error);
                // On error, complete without confirmation
                handleStatusUpdateInModal(selectedOrder.id, status);
            } finally {
                setCheckingPayment(false);
            }
        } else {
            // Direct update for other statuses
            handleStatusUpdateInModal(selectedOrder.id, status);
        }
    };

    // Handle confirmation - proceed with COMPLETED status and confirm payment
    const handleConfirmCompletion = () => {
        setShowPaymentConfirmation(false);
        setPaymentInfo(null);
        handleStatusUpdateInModal(selectedOrder.id, 'COMPLETED', { paymentConfirmed: true });
    };

    // All possible statuses (removed IN_PROGRESS and FAILED)
    const allStatuses = [
        'PENDING', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED', 'COMPLETED', 'CANCELLED'
    ];

    // Define valid status transitions based on business rules
    const getValidNextStatuses = (currentStatus: string): string[] => {
        const statusTransitions: { [key: string]: string[] } = {
            'PENDING': ['ACCEPTED', 'PREPARING', 'READY', 'SERVED', 'COMPLETED', 'CANCELLED'],
            'ACCEPTED': ['PREPARING', 'READY', 'SERVED', 'COMPLETED', 'CANCELLED'],
            'PREPARING': ['READY', 'SERVED', 'COMPLETED', 'CANCELLED'],
            'READY': ['SERVED', 'COMPLETED', 'CANCELLED'],
            'SERVED': ['COMPLETED'],
            'COMPLETED': [], // Final state
            'CANCELLED': [] // Final state
        };
        return statusTransitions[currentStatus] || [];
    };

    const validNextStatuses = getValidNextStatuses(selectedOrder.status);

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] lg:p-responsive-md"
            onClick={(e) => {
                // Close modal when clicking outside (only on desktop)
                if (e.target === e.currentTarget && window.innerWidth >= 1024) {
                    onClose();
                }
            }}
        >
            <div
                className="bg-white dark:bg-gray-800 shadow-2xl w-full h-full lg:rounded-2xl lg:max-w-4xl lg:max-h-[90vh] overflow-y-auto relative"
                style={{
                    backgroundColor: theme.components.card.background,
                    paddingTop: 'env(safe-area-inset-top, 0px)',
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                    paddingLeft: 'env(safe-area-inset-left, 0px)',
                    paddingRight: 'env(safe-area-inset-right, 0px)',
                }}
            >
                {/* Floating Back Button - Mobile Only */}
                <button
                    onClick={onClose}
                    className="lg:hidden fixed flex items-center justify-center focus:outline-none transition-all duration-200"
                    style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: 'var(--text-primary)',
                        bottom: '100px',
                        left: '8px',
                        width: '36px',
                        height: '36px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        zIndex: 10000,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.2s ease',
                        opacity: 1,
                        transform: 'scale(1)',
                        padding: 0,
                        margin: 0,
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                        e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.transform = 'scale(1)';
                    }}
                    aria-label="Go back"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-5 h-5"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                </button>

                {/* Modal Header */}
                <div className="sticky top-0 bg-inherit p-responsive-lg border-b border-gray-200 dark:border-gray-700 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-xl lg:text-2xl font-bold flex items-center gap-2" style={{ color: theme.primary }}>
                        <span className="text-2xl" style={{ color: getOrderTypeDisplay(selectedOrder.customOrderId).color }}>
                            {getOrderTypeDisplay(selectedOrder.customOrderId).icon}
                        </span>
                        <span>Order {formatOrderId(selectedOrder.customOrderId, DisplayContext.VENDOR)} Details</span>
                    </h2>
                    <CloseButton onClick={onClose} size="lg" />
                </div>

                {/* Modal Content */}
                <div className="p-6">
                    {/* Order Summary */}
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <h3 className="font-semibold mb-2" style={{ color: theme.primary }}>Order Information</h3>
                                <p className="text-sm" style={{ color: theme.text }}>
                                    <span className="font-medium">Customer:</span> {
                                        selectedOrder.customerName && selectedOrder.isGuestOrder
                                            ? `${selectedOrder.customerName} (Guest - ${selectedOrder.customerPhone})`
                                            : selectedOrder.guestName
                                                ? `${selectedOrder.guestName} (Guest - ${selectedOrder.guestPhone})`
                                                : selectedOrder.user
                                                    ? `${selectedOrder.user.username} (ID: ${selectedOrder.user.id})`
                                                    : 'Walk-in Customer (Placed at Counter)'
                                    }
                                </p>
                                <p className="text-sm" style={{ color: theme.text }}>
                                    <span className="font-medium">Order Date:</span> {new Date(selectedOrder.createdAt).toLocaleString()}
                                </p>
                                <p className="text-sm" style={{ color: theme.text }}>
                                    <span className="font-medium">Delivery Type:</span> {selectedOrder.deliveryType?.replace('_', ' ').toLowerCase() || 'N/A'}
                                </p>
                                {/* Table - display as breadcrumb path */}
                                {(selectedOrder.tableName || selectedOrder.floorName || selectedOrder.sectionName) && (
                                    <p className="text-sm" style={{ color: theme.text }}>
                                        <span className="font-medium">Table:</span>{' '}
                                        {[selectedOrder.floorName, selectedOrder.sectionName, selectedOrder.tableName]
                                            .filter(Boolean)
                                            .join(' > ')}
                                    </p>
                                )}
                            </div>
                            <div>
                                <h3 className="font-semibold mb-2" style={{ color: theme.primary }}>Status & Total</h3>
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="text-sm font-medium" style={{ color: theme.text }}>Status:</span>
                                    <span
                                        className="px-3 py-1 text-xs font-semibold rounded-full"
                                        style={{
                                            backgroundColor: selectedOrder.status === 'PENDING' ? theme.warning + '33' :
                                                selectedOrder.status === 'COMPLETED' ? theme.success + '33' :
                                                    selectedOrder.status === 'CANCELLED' ? theme.danger + '33' :
                                                        theme.accent + '33',
                                            color: selectedOrder.status === 'PENDING' ? theme.warning :
                                                selectedOrder.status === 'COMPLETED' ? theme.success :
                                                    selectedOrder.status === 'CANCELLED' ? theme.danger :
                                                        theme.accent
                                        }}
                                    >
                                        {selectedOrder.status}
                                    </span>
                                </div>
                                {/* Cancellation reason - show for all cancelled orders */}
                                {selectedOrder.status && selectedOrder.status.toUpperCase() === 'CANCELLED' && (
                                    <div
                                        className="mb-3 p-3 rounded-lg text-sm"
                                        style={{ backgroundColor: `${theme.danger}15`, border: `1px solid ${theme.danger}30` }}
                                    >
                                        <span className="font-semibold" style={{ color: theme.danger }}>Cancellation Reason: </span>
                                        <span style={{ color: selectedOrder.cancellationReason ? theme.text : theme.textSecondary }}>
                                            {selectedOrder.cancellationReason || 'No reason provided'}
                                        </span>
                                        {selectedOrder.cancelledAt && (
                                            <p className="text-xs mt-1" style={{ color: theme.textSecondary }}>
                                                Cancelled at: {new Date(selectedOrder.cancelledAt).toLocaleString()}
                                            </p>
                                        )}
                                    </div>
                                )}
                                <p className="text-lg font-bold" style={{ color: theme.success }}>
                                    Total: {CURRENCY.symbol}{(selectedOrder.total || 0).toFixed(2)}
                                </p>
                                {/* Only show Invoice button for completed/ready orders */}
                                {(selectedOrder.status === 'COMPLETED' || selectedOrder.status === 'READY') && (
                                    <button
                                        onClick={() => {
                                            onClose();
                                            setInvoiceOrderId(selectedOrder.id);
                                            setShowInvoiceModal(true);
                                        }}
                                        className="mt-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1"
                                        style={{
                                            backgroundColor: theme.primary,
                                            color: theme.button.primary.text
                                        }}
                                        onMouseOver={(e) => {
                                            e.currentTarget.style.backgroundColor = theme.button.primary.hover;
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.backgroundColor = theme.primary;
                                        }}
                                    >
                                        <DocumentTextIcon className="h-4 w-4" />
                                        View Invoice
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Order-level Special Instructions */}
                    {selectedOrder.orderNotes && (
                        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-700">
                            <div className="flex items-start gap-3">
                                <span className="text-xl flex-shrink-0">📝</span>
                                <div>
                                    <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-1">Special Instructions</h3>
                                    <p className="text-sm text-yellow-700 dark:text-yellow-400 italic">{selectedOrder.orderNotes}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Time Analytics */}
                    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 rounded-xl border border-blue-200 dark:border-gray-600">
                        <h3 className="text-lg font-semibold mb-4 flex items-center" style={{ color: theme.primary }}>
                            <span className="mr-2">⏱️</span>
                            Time Analytics
                        </h3>
                        {modalLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: theme.primary }}></div>
                                <span className="ml-3" style={{ color: theme.text }}>Loading analytics...</span>
                            </div>
                        ) : orderAnalytics ? (
                            <div className="space-y-6">
                                {/* Duration Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {orderAnalytics.orderToAcceptedTime && (
                                        <div className="bg-white dark:bg-gray-600 p-3 rounded-lg">
                                            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Order to Accepted</p>
                                            <p className="text-sm font-bold" style={{ color: theme.primary }}>{orderAnalytics.orderToAcceptedTime}</p>
                                        </div>
                                    )}
                                    {orderAnalytics.acceptedToPreparingTime && (
                                        <div className="bg-white dark:bg-gray-600 p-3 rounded-lg">
                                            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Accepted to Preparing</p>
                                            <p className="text-sm font-bold" style={{ color: theme.primary }}>{orderAnalytics.acceptedToPreparingTime}</p>
                                        </div>
                                    )}
                                    {orderAnalytics.preparingToReadyTime && (
                                        <div className="bg-white dark:bg-gray-600 p-3 rounded-lg">
                                            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Preparing to Ready</p>
                                            <p className="text-sm font-bold" style={{ color: theme.primary }}>{orderAnalytics.preparingToReadyTime}</p>
                                        </div>
                                    )}
                                    {orderAnalytics.readyToCompletedTime && (
                                        <div className="bg-white dark:bg-gray-600 p-3 rounded-lg">
                                            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Ready to Completed</p>
                                            <p className="text-sm font-bold" style={{ color: theme.primary }}>{orderAnalytics.readyToCompletedTime}</p>
                                        </div>
                                    )}
                                    {orderAnalytics.totalTime && (
                                        <div className="bg-white dark:bg-gray-600 p-3 rounded-lg md:col-span-2">
                                            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Total Order Time</p>
                                            <p className="text-lg font-bold" style={{ color: theme.success }}>{orderAnalytics.totalTime}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Order Timeline */}
                                <div className="bg-white dark:bg-gray-600 p-4 rounded-lg">
                                    <h4 className="text-md font-semibold mb-4 flex items-center" style={{ color: theme.primary }}>
                                        <span className="mr-2">📅</span>
                                        Order Timeline
                                    </h4>
                                    <div className="space-y-4">
                                        {/* Order Placed */}
                                        {orderAnalytics.timestamps.createdAt && (
                                            <div className="flex items-start gap-2 sm:gap-3">
                                                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                                    <span className="text-xs sm:text-sm">📝</span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                                        <p className="text-sm font-medium" style={{ color: theme.text }}>Order Placed</p>
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
                                                            Created
                                                        </span>
                                                    </div>
                                                    <p className="text-xs" style={{ color: theme.gray[500] }}>
                                                        {new Date(orderAnalytics.timestamps.createdAt).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Order Accepted */}
                                        {orderAnalytics.timestamps.acceptedAt && (
                                            <div className="flex items-start gap-2 sm:gap-3">
                                                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                                                    <span className="text-xs sm:text-sm">✅</span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                                        <p className="text-sm font-medium" style={{ color: theme.text }}>Order Accepted</p>
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
                                                            Accepted
                                                        </span>
                                                    </div>
                                                    <p className="text-xs" style={{ color: theme.gray[500] }}>
                                                        {new Date(orderAnalytics.timestamps.acceptedAt).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Order Preparing */}
                                        {orderAnalytics.timestamps.preparingAt && (
                                            <div className="flex items-start gap-2 sm:gap-3">
                                                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                                                    <span className="text-xs sm:text-sm">👨‍🍳</span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                                        <p className="text-sm font-medium" style={{ color: theme.text }}>Started Preparing</p>
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-orange-100 text-orange-800 whitespace-nowrap">
                                                            Preparing
                                                        </span>
                                                    </div>
                                                    <p className="text-xs" style={{ color: theme.gray[500] }}>
                                                        {new Date(orderAnalytics.timestamps.preparingAt).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Order Ready */}
                                        {orderAnalytics.timestamps.readyAt && (
                                            <div className="flex items-start gap-2 sm:gap-3">
                                                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                                                    <span className="text-xs sm:text-sm">🔔</span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                                        <p className="text-sm font-medium" style={{ color: theme.text }}>Order Ready</p>
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-purple-100 text-purple-800 whitespace-nowrap">
                                                            Ready
                                                        </span>
                                                    </div>
                                                    <p className="text-xs" style={{ color: theme.gray[500] }}>
                                                        {new Date(orderAnalytics.timestamps.readyAt).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Order Completed */}
                                        {orderAnalytics.timestamps.completedAt && (
                                            <div className="flex items-start gap-2 sm:gap-3">
                                                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center">
                                                    <span className="text-xs sm:text-sm">🎉</span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                                        <p className="text-sm font-medium" style={{ color: theme.text }}>Order Completed</p>
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-emerald-100 text-emerald-800 whitespace-nowrap">
                                                            Completed
                                                        </span>
                                                    </div>
                                                    <p className="text-xs" style={{ color: theme.gray[500] }}>
                                                        {new Date(orderAnalytics.timestamps.completedAt).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8" style={{ color: theme.text }}>
                                <p className="text-sm">No time analytics available for this order.</p>
                                <p className="text-xs mt-1" style={{ color: theme.gray[500] }}>
                                    Analytics are generated when orders go through status changes.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Order Items */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-4" style={{ color: theme.primary }}>Items Ordered</h3>
                        {selectedOrder.items && selectedOrder.items.length > 0 ? (
                            <div className="space-y-3">
                                {selectedOrder.items.map((item) => (
                                    <div key={item.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <div className="flex justify-between items-start mb-2">
                                            {/* Item thumbnail */}
                                            {(item.imageUrl || item.menuItem?.imageUrl) && (
                                                <div className="w-10 h-10 rounded-lg flex-shrink-0 overflow-hidden mr-3">
                                                    <MenuItemImage imageUrl={item.imageUrl || item.menuItem?.imageUrl} itemName={item.itemName || item.menuItem?.name || 'Item'} size="thumbnail" />
                                                </div>
                                            )}
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                                    <p className="font-semibold" style={{ color: theme.text }}>{item.itemName || item.menuItem?.name || 'Unknown Item'}</p>
                                                    {item.portionName && (
                                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded uppercase font-medium">
                                                            {item.portionName}
                                                        </span>
                                                    )}
                                                    {(item.requestedSpiceLevel || item.spiceLevel) && (
                                                        <SpiceLevelBadge
                                                            spiceLevel={item.requestedSpiceLevel || item.spiceLevel}
                                                            size="small"
                                                        />
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-600 dark:text-gray-300">Quantity: {item.quantity}</p>
                                            </div>
                                            <p className="font-semibold" style={{ color: theme.text }}>
                                                {CURRENCY.symbol}{(item.price * item.quantity).toFixed(2)}
                                            </p>
                                        </div>
                                        {item.toppings && item.toppings.length > 0 && (
                                            <div className="mt-2 pl-2 border-l-2 border-gray-300 dark:border-gray-600">
                                                <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Toppings:</p>
                                                <ul className="list-disc list-inside ml-2">
                                                    {item.toppings.map((topping, idx) => (
                                                        <li key={typeof topping === 'string' ? idx : topping.id} className="text-xs text-gray-500 dark:text-gray-400">
                                                            {typeof topping === 'string' ? topping : topping.name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {item.comment && (
                                            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                                                <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Comments:</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 italic">{item.comment}</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400 italic">No items found in this order.</p>
                        )}
                    </div>

                    {/* KOTs Section */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center" style={{ color: theme.primary }}>
                            <span className="mr-2">🧾</span>
                            Kitchen Order Tickets (KOTs)
                        </h3>
                        {orderKOTs && orderKOTs.length > 0 ? (
                            <div className="space-y-3">
                                {orderKOTs.map((kot) => {
                                    const getKOTStatusStyle = (status: string) => {
                                        const styles = {
                                            'PENDING': { bg: '#FEF3C7', border: 'var(--status-warn)', text: '#8A5411', icon: '⏳' },
                                            'ACKNOWLEDGED': { bg: '#DBEAFE', border: 'var(--status-info-border)', text: '#2D6CB5', icon: '👀' },
                                            'STARTED': { bg: '#FED7AA', border: '#FB923C', text: '#E0891E', icon: '👨‍🍳' }, /* basil-todo */
                                            'READY': { bg: '#D1FAE5', border: 'var(--status-ok-border)', text: '#1E8E5A', icon: '✅' },
                                            'SERVED': { bg: '#F3F4F6', border: '#6B7280', text: '#4B5563', icon: '🍽️' }, /* basil-todo */
                                            'CANCELLED': { bg: '#FEE2E2', border: 'var(--status-bad)', text: '#D2453C', icon: '❌' }
                                        };
                                        return styles[status as keyof typeof styles] || styles['PENDING'];
                                    };

                                    const statusStyle = getKOTStatusStyle(kot.status);
                                    const isExpanded = expandedKOTs.has(kot.id);
                                    const isUpdating = updatingKOTs.has(kot.id);

                                    return (
                                        <div
                                            key={kot.id}
                                            className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border-l-4 transition-colors"
                                            style={{ borderLeftColor: statusStyle.border }}
                                        >
                                            <div className="mb-2">
                                                {/* Header row with KOT number and status - clickable to expand */}
                                                <div
                                                    className="flex items-start gap-2 mb-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 -m-2 p-2 rounded"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setExpandedKOTs(prev => {
                                                            const updated = new Set(prev);
                                                            if (updated.has(kot.id)) {
                                                                updated.delete(kot.id);
                                                            } else {
                                                                updated.add(kot.id);
                                                            }
                                                            return updated;
                                                        });
                                                    }}
                                                >
                                                    {/* Expand/collapse icon on the left */}
                                                    <span className="text-sm flex-shrink-0 mt-0.5" style={{ color: theme.text }}>
                                                        {isExpanded ? '▼' : '▶'}
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                                            <p className="font-semibold text-sm sm:text-base" style={{ color: theme.text }}>
                                                                KOT #{kot.kotNumber}
                                                            </p>
                                                            <span className="text-xs sm:text-sm text-gray-500">- {kot.kitchenName}</span>
                                                            <span
                                                                className="px-2 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full inline-flex items-center gap-1 whitespace-nowrap"
                                                                style={{
                                                                    backgroundColor: statusStyle.bg,
                                                                    color: statusStyle.text,
                                                                    border: `1px solid ${statusStyle.border}`
                                                                }}
                                                            >
                                                                <span>{statusStyle.icon}</span>
                                                                {kot.status}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {/* Print KOT button */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handlePrintKOT(kot, e);
                                                        }}
                                                        disabled={printingKOTs.has(kot.id)}
                                                        className="flex-shrink-0 px-3 py-1.5 rounded-md bg-blue-500 hover:bg-blue-600 text-white transition-colors disabled:opacity-50 flex items-center gap-1.5 text-sm font-medium"
                                                        title={`Print KOT ${kot.kotNumber}`}
                                                    >
                                                        {printingKOTs.has(kot.id) ? (
                                                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                        ) : (
                                                            <PrinterIcon className="w-4 h-4" />
                                                        )}
                                                        Print
                                                    </button>
                                                </div>

                                                {/* Info section - responsive layout */}
                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                                                    <span>Created: {new Date(kot.createdAt).toLocaleString()}</span>
                                                    <span>•</span>
                                                    <span>{kot.items.length} item{kot.items.length !== 1 ? 's' : ''}</span>
                                                    {kot.priority && kot.priority !== 'NORMAL' && (
                                                        <>
                                                            <span>•</span>
                                                            <span className="font-medium" style={{
                                                                color: kot.priority === 'HIGH' ? '#EF4444' : kot.priority === 'URGENT' ? '#DC2626' : theme.text
                                                            }}>
                                                                {kot.priority}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Expandable Content */}
                                            {isExpanded && (
                                                <>
                                                    {/* KOT Items - Full List */}
                                                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                                        <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Items:</p>
                                                        <div className="space-y-1">
                                                            {kot.items.map((kotItem) => (
                                                                <div key={kotItem.id}>
                                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                        {kotItem.quantity}x {kotItem.itemName}
                                                                        {kotItem.portion && <span className="ml-1 text-gray-400">({kotItem.portion})</span>}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {kot.specialInstructions && (
                                                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                                                            <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Special Instructions:</p>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 italic">{kot.specialInstructions}</p>
                                                        </div>
                                                    )}

                                                    {/* Mark as Buttons */}
                                                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                                        <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Update Status:</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {['ACKNOWLEDGED', 'PREPARING', 'READY', 'SERVED'].map((status) => {
                                                                const isCurrentStatus = kot.status === status;
                                                                const statusStyle = getKOTStatusStyle(status);
                                                                return (
                                                                    <button
                                                                        key={status}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (!isCurrentStatus && !isUpdating) {
                                                                                handleKOTStatusUpdate(kot.id, status);
                                                                            }
                                                                        }}
                                                                        disabled={isCurrentStatus || isUpdating}
                                                                        className="px-3 py-1.5 text-xs font-medium rounded transition-all"
                                                                        style={{
                                                                            backgroundColor: isCurrentStatus ? statusStyle.bg : '#FFFFFF',
                                                                            color: isCurrentStatus ? statusStyle.text : theme.text,
                                                                            border: `1px solid ${statusStyle.border}`,
                                                                            opacity: isCurrentStatus ? 1 : 0.7,
                                                                            cursor: isCurrentStatus || isUpdating ? 'not-allowed' : 'pointer'
                                                                        }}
                                                                    >
                                                                        {isUpdating && !isCurrentStatus ? '...' : status}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>

                                                    {/* View in KDS Link */}
                                                    <div className="mt-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/vendor/kitchen?kotId=${kot.id}`);
                                                                onClose();
                                                            }}
                                                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                                                        >
                                                            View in Kitchen Display System →
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-8" style={{ color: theme.text }}>
                                <p className="text-sm">No KOTs found for this order.</p>
                                <p className="text-xs mt-1" style={{ color: theme.gray[500] }}>
                                    KOTs are created when orders are accepted and sent to kitchens.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Status Update Section */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <h3 className="text-lg font-semibold mb-4" style={{ color: theme.primary }}>Update Order Status</h3>

                        {/* Visual Status Cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3 mb-4">
                            {allStatuses.map((status: string) => {
                                const isCurrent = selectedOrder.status === status;
                                const isValidTransition = validNextStatuses.includes(status);
                                const isClickable = isValidTransition && !isCurrent;


                                // Define colors for each status (Kitchen Display System inspired)
                                const getStatusStyle = (status: string) => {
                                    const styles = {
                                        'PENDING': { bg: '#FEF3C7', border: 'var(--status-warn)', text: '#8A5411', icon: '⏳' },
                                        'ACCEPTED': { bg: '#DBEAFE', border: 'var(--status-info-border)', text: '#2D6CB5', icon: '✅' },
                                        'PREPARING': { bg: '#FED7AA', border: '#FB923C', text: '#E0891E', icon: '👨‍🍳' }, /* basil-todo */
                                        'READY': { bg: '#D1FAE5', border: 'var(--status-ok-border)', text: '#1E8E5A', icon: '🟢' },
                                        'SERVED': { bg: '#E0E7FF', border: '#818CF8', text: '#6D5BD0', icon: '🍽️' }, /* basil-todo */
                                        'COMPLETED': { bg: '#F3F4F6', border: '#6B7280', text: '#4B5563', icon: '✅' }, /* basil-todo */
                                        'CANCELLED': { bg: '#FEE2E2', border: 'var(--status-bad)', text: '#D2453C', icon: '🚫' }
                                    };
                                    return styles[status as keyof typeof styles] || styles['PENDING'];
                                };

                                const statusStyle = getStatusStyle(status);
                                const displayName = status.replace('_', ' ');

                                return (
                                    <button
                                        key={status}
                                        onClick={() => {
                                            // Check for payment confirmation before completing
                                            if (isClickable && !modalLoading && !checkingPayment) {
                                                handleStatusClick(status);
                                            }
                                        }}
                                        disabled={modalLoading || checkingPayment || !isClickable}
                                        className={`relative p-2.5 rounded-lg border-2 transition-all duration-200 focus:outline-none ${!isClickable ? 'disabled:opacity-50 disabled:cursor-not-allowed' : 'hover:shadow-md cursor-pointer'
                                            }`}
                                        style={{
                                            backgroundColor: statusStyle.bg,
                                            borderColor: isCurrent ? statusStyle.border : isValidTransition ? statusStyle.border : 'var(--border-subtle)',
                                            borderWidth: isCurrent || isValidTransition ? '2px' : '1px',
                                            opacity: isCurrent ? 0.8 : isValidTransition ? 1 : 0.4
                                        }}
                                    >
                                        {/* Current Status Badge */}
                                        {isCurrent && (
                                            <div
                                                className="absolute -top-2 -right-2 px-1.5 py-0.5 text-[9px] font-bold rounded-full text-white"
                                                style={{ backgroundColor: statusStyle.border }}
                                            >
                                                CURRENT
                                            </div>
                                        )}

                                        {/* Valid Next Step Badge */}
                                        {isValidTransition && !isCurrent && (
                                            <div
                                                className="absolute -top-2 -right-2 px-1.5 py-0.5 text-[9px] font-bold rounded-full text-white"
                                                style={{ backgroundColor: statusStyle.border }}
                                            >
                                                NEXT
                                            </div>
                                        )}



                                        {/* Status Icon */}
                                        <div className="text-lg mb-1 text-center">
                                            {statusStyle.icon}
                                        </div>

                                        {/* Status Text */}
                                        <div
                                            className="text-xs font-semibold text-center leading-tight"
                                            style={{ color: statusStyle.text }}
                                        >
                                            {displayName}
                                        </div>


                                    </button>
                                );
                            })}
                        </div>



                        {modalLoading && (
                            <div className="text-center">
                                <span className="text-sm" style={{ color: theme.text }}>Updating order status...</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Payment Confirmation Popup */}
            {showPaymentConfirmation && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[10000]"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowPaymentConfirmation(false);
                        }
                    }}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
                        style={{ backgroundColor: theme.components?.card?.background || '#ffffff' }}
                    >
                        {/* Header */}
                        <div
                            className="p-4 border-b flex items-center gap-3"
                            style={{ borderColor: theme.primary + '50', backgroundColor: theme.primary + '10' }}
                        >
                            <div
                                className="p-2 rounded-full"
                                style={{ backgroundColor: theme.primary + '20' }}
                            >
                                <ExclamationTriangleIcon className="h-6 w-6" style={{ color: theme.primary }} />
                            </div>
                            <h3 className="text-lg font-semibold" style={{ color: theme.text }}>
                                Confirm Payment
                            </h3>
                        </div>

                        {/* Content */}
                        <div className="p-5">
                            <p className="text-sm mb-4" style={{ color: theme.textSecondary || theme.text }}>
                                Has the customer paid for this session?
                            </p>

                            <div
                                className="p-4 rounded-lg mb-4"
                                style={{ backgroundColor: theme.gray?.[100] || '#f3f4f6' }}
                            >
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm" style={{ color: theme.textSecondary || theme.text }}>Session Total:</span>
                                    <span className="font-medium" style={{ color: theme.text }}>
                                        {formatCurrency(paymentInfo?.sessionTotalAmount || 0)}
                                    </span>
                                </div>
                                {(paymentInfo?.sessionDiscountAmount || 0) > 0 && (
                                    <div className="flex justify-between mb-2">
                                        <span className="text-sm" style={{ color: theme.success }}>🏷️ Discount Applied:</span>
                                        <span className="font-medium" style={{ color: theme.success }}>
                                            -{formatCurrency(paymentInfo?.sessionDiscountAmount || 0)}
                                        </span>
                                    </div>
                                )}
                                {(paymentInfo?.sessionPaidAmount || 0) > 0 && (
                                    <div className="flex justify-between mb-2">
                                        <span className="text-sm" style={{ color: theme.textSecondary || theme.text }}>Already Paid:</span>
                                        <span className="font-medium" style={{ color: theme.success }}>
                                            {formatCurrency(paymentInfo?.sessionPaidAmount || 0)}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between pt-2 border-t" style={{ borderColor: theme.gray?.[300] || '#d1d5db' }}>
                                    <span className="text-sm font-semibold" style={{ color: theme.primary }}>To Collect:</span>
                                    <span className="font-bold" style={{ color: theme.primary }}>
                                        {formatCurrency(paymentInfo?.sessionRemainingAmount || 0)}
                                    </span>
                                </div>
                                {(() => {
                                    // When taxes are recalculated on the discounted amount, the three
                                    // figures aren't pure subtraction — say so, or the gap looks wrong.
                                    const total = paymentInfo?.sessionTotalAmount || 0;
                                    const disc = paymentInfo?.sessionDiscountAmount || 0;
                                    const paid = paymentInfo?.sessionPaidAmount || 0;
                                    const remaining = paymentInfo?.sessionRemainingAmount || 0;
                                    return disc > 0 && Math.abs(total - disc - paid - remaining) > 0.01 ? (
                                        <p className="text-xs mt-2" style={{ color: theme.textSecondary || theme.text }}>
                                            Taxes are recalculated on the discounted amount — see the invoice for the full breakdown.
                                        </p>
                                    ) : null;
                                })()}
                            </div>

                            <p className="text-sm" style={{ color: theme.textSecondary || theme.text }}>
                                Review the invoice and click <strong>Paid</strong> to confirm payment received.
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="p-4 border-t flex flex-col gap-3" style={{ borderColor: theme.gray?.[200] || '#e5e7eb' }}>
                            {/* View Invoice Button - Full width */}
                            <button
                                onClick={() => {
                                    // Open invoice modal for this order with all session orders (including completed)
                                    setInvoiceOrderId(selectedOrder.id);
                                    setInvoiceIncludeCompleted?.(true); // Show full session invoice
                                    setShowInvoiceModal(true);
                                }}
                                className="w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                style={{
                                    backgroundColor: theme.primary + '15',
                                    color: theme.primary,
                                    border: `1px solid ${theme.primary}40`
                                }}
                            >
                                <DocumentTextIcon className="h-5 w-5" />
                                View Complete Invoice
                            </button>

                            {/* Cancel and Paid buttons */}
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => {
                                        setShowPaymentConfirmation(false);
                                        setPaymentInfo(null);
                                    }}
                                    className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                    style={{
                                        backgroundColor: theme.gray?.[200] || '#e5e7eb',
                                        color: theme.text
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmCompletion}
                                    className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                    style={{
                                        backgroundColor: theme.success,
                                        color: 'var(--text-inverse)'
                                    }}
                                >
                                    Paid
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderDetailModal;
